import React, { useMemo, useState } from "react";
import dayjs from "dayjs";

import { useGetNPSDashboardQuery, useGetNPSResponsesQuery } from "../../apis/nps";
import NPSDashboardHeader from "./components/NPSDashboardHeader";
import NPSGauge from "./components/NPSGauge";
import NPSStatTiles from "./components/NPSStatTiles";
import NPSScoreDistribution from "./components/NPSScoreDistribution";
import NPSTrendChart from "./components/NPSTrendChart";
import NPSRecentResponses from "./components/NPSRecentResponses";
import NPSAllResponsesView from "./components/NPSAllResponsesView";

import { DEFAULT_PERIOD, SEGMENT, daysFromPeriod } from "./constants/npsConstants";
import { downloadResponsesAsCsv } from "./utils/npsUtils";

import "./NPSOverview.css";

const NPSOverview = () => {
  const [view, setView] = useState("dashboard");
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [city, setCity] = useState("");
  const [presetScore, setPresetScore] = useState(null);

  const dateRangeDays = daysFromPeriod(period);

  const dashboardParams = useMemo(() => {
    const params = {
      start_date: dayjs().subtract(dateRangeDays, "day").format("YYYY-MM-DD"),
      end_date: dayjs().format("YYYY-MM-DD"),
    };
    if (city) params.city = city;
    return params;
  }, [dateRangeDays, city]);

  const { data: dashboardData, isFetching: isDashboardLoading } = useGetNPSDashboardQuery(dashboardParams);

  
  const { data: recentResponsesData, isFetching: isRecentResponsesLoading } = useGetNPSResponsesQuery({
    ...dashboardParams,
    page: 1,
  });

  const cityOptions = dashboardData?.data?.filters?.cities || [];
  const periodOptions = dashboardData?.data?.filters?.periods || [];

  const totalResponses = dashboardData?.data?.total_responses || 0;

  const distribution = useMemo(() => {
    const dist = {};
    for (let s = 0; s <= 10; s++) dist[s] = 0;
    (dashboardData?.data?.score_distribution || []).forEach((d) => { dist[d.score] = d.count; });
    return dist;
  }, [dashboardData]);

  const segmentCounts = useMemo(() => ({
    [SEGMENT.PROMOTER]: dashboardData?.data?.promoters || 0,
    [SEGMENT.PASSIVE]: dashboardData?.data?.passives || 0,
    [SEGMENT.DETRACTOR]: dashboardData?.data?.detractors || 0,
  }), [dashboardData]);

  const trendHistory = useMemo(() => (dashboardData?.data?.nps_trend || []).map((t) => {
    const monthTotal = t.total_responses || 0;
    return {
      month: dayjs(t.month).format("MMM YY"),
      nps: t.nps_score,
      avg: t.average_score,
      count: monthTotal,
      promoters: monthTotal ? Math.round((t.promoters / monthTotal) * 100) : 0,
      detractors: monthTotal ? Math.round((t.detractors / monthTotal) * 100) : 0,
    };
  }), [dashboardData]);

  const npsDelta = useMemo(() => {
    const trend = dashboardData?.data?.nps_trend || [];
    if (trend.length < 2) return 0;
    return trend.at(-1).nps_score - trend.at(-2).nps_score;
  }, [dashboardData]);

  const recentResponses = (recentResponsesData?.data?.results || []).slice(0, 6);

  const goToResponses = (score = null) => {
    setPresetScore(score);
    setView("responses");
    window.scrollTo({ top: 0 });
  };

  const goToDashboard = () => {
    setView("dashboard");
    window.scrollTo({ top: 0 });
  };

  const handleExportDashboard = () => {
    downloadResponsesAsCsv(recentResponses, "nps-responses.csv");
  };

  if (view === "responses") {
    return (
      <div className="nps-overview-page">
        <NPSAllResponsesView
          total={totalResponses}
          presetScore={presetScore}
          dateRangeDays={dateRangeDays}
          city={city}
          cityOptions={cityOptions}
          onBack={goToDashboard}
        />
      </div>
    );
  }

  return (
    <div className="nps-overview-page">
      <NPSDashboardHeader
        period={period}
        onPeriodChange={setPeriod}
        periodOptions={periodOptions}
        city={city}
        onCityChange={setCity}
        cityOptions={cityOptions}
        onExport={handleExportDashboard}
      />

      <div className="nps-top-grid">
        <NPSGauge
          score={dashboardData?.data?.overall_nps_rating || 0}
          deltaVsLastMonth={npsDelta}
          averageScore={dashboardData?.data?.average_score || 0}
          isLoading={isDashboardLoading}
        />
        <NPSStatTiles
          total={totalResponses}
          responseRate={dashboardData?.data?.response_rate || 0}
          eligibleResponses={dashboardData?.data?.eligible_responses || 0}
          promoters={segmentCounts[SEGMENT.PROMOTER]}
          passives={segmentCounts[SEGMENT.PASSIVE]}
          detractors={segmentCounts[SEGMENT.DETRACTOR]}
          isLoading={isDashboardLoading}
        />
      </div>

      <NPSScoreDistribution
        distribution={distribution}
        total={totalResponses}
        promoters={segmentCounts[SEGMENT.PROMOTER]}
        passives={segmentCounts[SEGMENT.PASSIVE]}
        detractors={segmentCounts[SEGMENT.DETRACTOR]}
        onSelectScore={goToResponses}
        isLoading={isDashboardLoading}
      />

      <NPSTrendChart history={trendHistory} isLoading={isDashboardLoading} />

      <NPSRecentResponses
        responses={recentResponses}
        isLoading={isRecentResponsesLoading}
        onViewAll={() => goToResponses(null)}
      />
    </div>
  );
};

export default NPSOverview;
